import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '../shared/services/error.service';
import { buildScheduleApiUrl } from '../utils/schedule-api-url.util';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  message?: string;
}

export interface Schedule {
  Id?: number;
  Name: string;
  Description: string;
  GroupId: number;
  Sunday: boolean;
  Monday: boolean;
  Tuesday: boolean;
  Wednesday: boolean;
  Thursday: boolean;
  Friday: boolean;
  Saturday: boolean;
  AppointmentsByHours: number;
  TimeIntervalMinutes: number;
  CanExternalLink: boolean;
  CreatedBy: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
  IsDeleted?: boolean;
  DeletedAt?: string;
  DeletedBy?: string;
  ExcludedDays?: any;
  TimeSlots?: any;
}

export interface ExcludedDay {
  Id?: number;
  ScheduleId: number;
  Date: string;
}

export interface TimeSlot {
  Id?: number;
  ScheduleId: number;
  Date: string;
  StartTime: string;
  EndTime: string;
  Capacity: number;
  BookedCount: number;
}

export interface Appointment {
  Id?: number;
  ScheduleId: number | null;
  ScheduleTimeSlotId: number | null;
  Date: string;
  StartTime: string;
  EndTime: string;
  Subject: string;
  Description: string;
  CustomerId: number;
  StaffId: number;
  /** Vínculo com OS — ao gravar, backend marca IsScheduled na ServiceOrder. */
  ServiceOrderId?: number | null;
  VehicleId?: number | null;
  Plate?: string | null;
  B2BContractId?: number | null;
  B2BContractDriverId?: number | null;
  DriverName?: string | null;
  ExternalServiceProviderId?: number | null;
  ExternalServiceProviderName?: string | null;
  PartnerNotes?: string | null;
  /** Solicitação de guincho (flag tipada). */
  IsGuinchoRequest?: boolean;
  /** Data/hora do comparecimento ou registro de falta (retroativo). */
  AttendedAt?: string | null;
  Status: string;
  IsDeleted: boolean;
  CreatedAt?: string;
  UpdatedAt?: string;
  CreatedBy?: string | null;
  UpdatedBy?: string | null;
  DeletedAt?: string | null;
  DeletedBy?: string | null;
}

export interface AppointmentHistoryEntry {
  Id: number;
  AppointmentId: number;
  EventType: string;
  Message: string;
  OldValue?: string | null;
  NewValue?: string | null;
  CreatedAt: string;
  CreatedBy?: string | null;
}

// Dashboard
export interface AppointmentsDashboard {
  scheduleId: number;
  totalAppointments: number;
  byStatus: Array<{ status: string; count: number }>;
  topDays: {
    take: number;
    items: Array<{ date: string; count: number }>;
  };
}

@Injectable({ providedIn: 'root' })
export class SchedulesService {
  constructor(private http: HttpClient, private errorService: ErrorService) {}

  getAll(page: number = 1, pageSize: number = 10): Observable<Schedule[]> {
    const url = buildScheduleApiUrl('schedules', { page, pageSize });

    return this.http.get<ApiResponse<Schedule[]>>(url)
      .pipe(
        map(response => response.data || []),
        catchError(err => this.handleError(err))
      );
  }

  getById(id: number): Observable<Schedule> {
    return this.http.get<ApiResponse<Schedule>>(buildScheduleApiUrl(`schedules/${id}`))
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  create(data: Schedule): Observable<Schedule> {
    return this.http.post<ApiResponse<Schedule>>(buildScheduleApiUrl('schedules'), data)
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  update(id: number, data: Schedule): Observable<Schedule> {
    return this.http.put<ApiResponse<Schedule>>(buildScheduleApiUrl(`schedules/${id}`), data)
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<ApiResponse<void>>(buildScheduleApiUrl(`schedules/${id}`))
      .pipe(
        map(response => {
          return response.data;
        }),
        catchError(err => {
          console.error('❌ Erro ao deletar agenda:', err);
          return this.handleError(err);
        })
      );
  }

  getAllExcludedDays(): Observable<ExcludedDay[]> {
    return this.http.get<ApiResponse<ExcludedDay[]>>(buildScheduleApiUrl('excluded-days'))
      .pipe(
        map(response => response.data || []),
        catchError(err => this.handleError(err))
      );
  }

  getExcludedDayById(id: number): Observable<ExcludedDay> {
    return this.http.get<ApiResponse<ExcludedDay>>(buildScheduleApiUrl(`excluded-days/${id}`))
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  createExcludedDay(data: ExcludedDay): Observable<ExcludedDay> {
    return this.http.post<ApiResponse<ExcludedDay>>(buildScheduleApiUrl('excluded-days'), data)
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  updateExcludedDay(id: number, data: ExcludedDay): Observable<ExcludedDay> {
    return this.http.put<ApiResponse<ExcludedDay>>(buildScheduleApiUrl(`excluded-days/${id}`), data)
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  deleteExcludedDay(id: number): Observable<void> {
    return this.http.delete<ApiResponse<void>>(buildScheduleApiUrl(`excluded-days/${id}`))
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  // Time slots
  getAllTimeSlots(scheduleId?: number, date?: string): Observable<TimeSlot[]> {
    const url = buildScheduleApiUrl('time-slots', { scheduleId, date, page: 1, pageSize: 2000 });

    return this.http.get<ApiResponse<TimeSlot[]>>(url)
      .pipe(
        map(response => (response.data || []).map(s => this.mapTimeSlot(s))),
        catchError(err => this.handleError(err))
      );
  }

  getTimeSlotById(id: number): Observable<TimeSlot> {
    return this.http.get<ApiResponse<TimeSlot>>(buildScheduleApiUrl(`time-slots/${id}`))
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  createTimeSlot(data: TimeSlot): Observable<TimeSlot> {
    return this.http.post<any>(buildScheduleApiUrl('time-slots'), data)
      .pipe(
        map(response => {
          if (response && response.data) {
            return response.data;
          }
          return response;
        }),
        catchError(err => this.handleError(err))
      );
  }

  updateTimeSlot(id: number, data: TimeSlot): Observable<TimeSlot> {
    return this.http.put<ApiResponse<TimeSlot>>(buildScheduleApiUrl(`time-slots/${id}`), data)
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  deleteTimeSlot(id: number): Observable<void> {
    return this.http.delete<ApiResponse<void>>(buildScheduleApiUrl(`time-slots/${id}`))
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  // Appointments
  getAllAppointments(): Observable<Appointment[]> {
    return this.http.get<ApiResponse<Appointment[]>>(buildScheduleApiUrl('appointments'))
      .pipe(
        map(response => (response.data || []).map(a => this.mapAppointment(a))),
        catchError(err => this.handleError(err))
      );
  }

  /** Solicitações do portal B2B em oficina parceira aguardando análise interna. */
  getPartnerPendingAppointments(): Observable<Appointment[]> {
    return this.http.get<ApiResponse<Appointment[]>>(buildScheduleApiUrl('appointments/partner-pending'))
      .pipe(
        map(response => (response.data || []).map(a => this.mapAppointment(a))),
        catchError(err => this.handleError(err))
      );
  }

  /** Fila centralizada de solicitações de guincho (não some após triagem). */
  getGuinchoRequests(includeClosed = false): Observable<Appointment[]> {
    return this.http.get<ApiResponse<Appointment[]>>(
      buildScheduleApiUrl('appointments/guincho-requests', { includeClosed: includeClosed ? 'true' : undefined })
    ).pipe(
      map(response => (response.data || []).map(a => this.mapAppointment(a))),
      catchError(err => this.handleError(err))
    );
  }

  getAppointmentHistory(id: number): Observable<AppointmentHistoryEntry[]> {
    return this.http.get<ApiResponse<AppointmentHistoryEntry[]>>(
      buildScheduleApiUrl(`appointments/${id}/history`)
    ).pipe(
      map(response => (response.data || []).map((h: any) => ({
        Id: h.Id ?? h.id,
        AppointmentId: h.AppointmentId ?? h.appointmentId,
        EventType: h.EventType ?? h.eventType ?? '',
        Message: h.Message ?? h.message ?? '',
        OldValue: h.OldValue ?? h.oldValue ?? null,
        NewValue: h.NewValue ?? h.newValue ?? null,
        CreatedAt: h.CreatedAt ?? h.createdAt,
        CreatedBy: h.CreatedBy ?? h.createdBy ?? null
      }))),
      catchError(err => this.handleError(err))
    );
  }

  /** Parceiros confirmados/agendados em um dia (ScheduleId nulo — agenda diária ERP). */
  getPartnerAppointmentsByDate(date: string): Observable<Appointment[]> {
    return this.http.get<ApiResponse<Appointment[]>>(
      buildScheduleApiUrl('appointments/partners', { date })
    ).pipe(
      map(response => (response.data || []).map(a => this.mapAppointment(a))),
      catchError(err => this.handleError(err))
    );
  }

  /** Parceiros no intervalo do calendário (contagem por dia). */
  getPartnerAppointmentsByRange(startDate: string, endDate: string): Observable<Appointment[]> {
    return this.http.get<ApiResponse<Appointment[]>>(
      buildScheduleApiUrl('appointments/partners', { startDate, endDate })
    ).pipe(
      map(response => (response.data || []).map(a => this.mapAppointment(a))),
      catchError(err => this.handleError(err))
    );
  }

  getAppointmentsByScheduleId(scheduleId: number): Observable<Appointment[]> {
    return this.http.get<ApiResponse<Appointment[]>>(
      buildScheduleApiUrl(`appointments/schedule/${scheduleId}`, { page: 1, pageSize: 5000 })
    ).pipe(
      map(response => (response.data || []).map(a => this.mapAppointment(a))),
      catchError(err => this.handleError(err))
    );
  }

  getAppointmentsByDocument(documentNumber: string, page: number = 1, pageSize: number = 10): Observable<Appointment[]> {
    const normalizedDocument = encodeURIComponent((documentNumber || '').trim());
    const url = buildScheduleApiUrl(`appointments/by-document/${normalizedDocument}`, { page, pageSize });

    return this.http.get<ApiResponse<Appointment[]>>(url)
      .pipe(
        map(response => (response.data || []).map(a => this.mapAppointment(a))),
        catchError(err => this.handleError(err))
      );
  }

  getAppointmentsDashboard(scheduleId: number): Observable<AppointmentsDashboard> {
    return this.http.get<ApiResponse<AppointmentsDashboard>>(buildScheduleApiUrl(`appointments/dashboard/${scheduleId}`))
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  getAppointmentById(id: number): Observable<Appointment> {
    return this.http.get<ApiResponse<Appointment>>(buildScheduleApiUrl(`appointments/${id}`))
      .pipe(
        map(response => this.mapAppointment(response.data)),
        catchError(err => this.handleError(err))
      );
  }

  createAppointment(data: Appointment): Observable<Appointment> {
    return this.http.post<ApiResponse<Appointment>>(buildScheduleApiUrl('appointments'), data)
      .pipe(
        map(response => this.mapAppointment(response.data)),
        catchError(err => this.handleError(err))
      );
  }

  updateAppointment(id: number, data: Appointment): Observable<Appointment> {
    return this.http.put<ApiResponse<Appointment>>(buildScheduleApiUrl(`appointments/${id}`), data)
      .pipe(
        map(response => this.mapAppointment(response.data)),
        catchError(err => this.handleError(err))
      );
  }

  deleteAppointment(id: number, deletedBy?: string | null): Observable<void> {
    const params = deletedBy ? { deletedBy } : undefined;
    return this.http.delete<ApiResponse<void>>(buildScheduleApiUrl(`appointments/${id}`, params))
      .pipe(
        map(response => response.data),
        catchError(err => this.handleError(err))
      );
  }

  private mapAppointment(dto: any): Appointment {
    if (!dto) {
      return dto;
    }
    const start = this.normalizeTime(dto.StartTime ?? dto.startTime);
    const end = this.normalizeTime(dto.EndTime ?? dto.endTime);
    const dateRaw = dto.Date ?? dto.date ?? '';
    return {
      ...dto,
      Id: dto.Id ?? dto.id,
      ScheduleId: dto.ScheduleId ?? dto.scheduleId,
      ScheduleTimeSlotId: dto.ScheduleTimeSlotId ?? dto.scheduleTimeSlotId,
      Date: typeof dateRaw === 'string' ? dateRaw : String(dateRaw || ''),
      StartTime: start,
      EndTime: end,
      Subject: dto.Subject ?? dto.subject ?? '',
      Description: dto.Description ?? dto.description ?? '',
      CustomerId: dto.CustomerId ?? dto.customerId ?? 0,
      StaffId: dto.StaffId ?? dto.staffId ?? 0,
      ServiceOrderId: dto.ServiceOrderId ?? dto.serviceOrderId ?? null,
      VehicleId: dto.VehicleId ?? dto.vehicleId ?? null,
      Plate: dto.Plate ?? dto.plate ?? null,
      B2BContractId: dto.B2BContractId ?? dto.b2BContractId ?? dto.b2bContractId ?? null,
      B2BContractDriverId: dto.B2BContractDriverId ?? dto.b2BContractDriverId ?? dto.b2bContractDriverId ?? null,
      DriverName: dto.DriverName ?? dto.driverName ?? null,
      ExternalServiceProviderId: dto.ExternalServiceProviderId ?? dto.externalServiceProviderId ?? null,
      ExternalServiceProviderName: dto.ExternalServiceProviderName ?? dto.externalServiceProviderName ?? null,
      PartnerNotes: dto.PartnerNotes ?? dto.partnerNotes ?? null,
      IsGuinchoRequest: !!(dto.IsGuinchoRequest ?? dto.isGuinchoRequest),
      AttendedAt: dto.AttendedAt ?? dto.attendedAt ?? null,
      Status: dto.Status ?? dto.status ?? 'Agendado',
      IsDeleted: !!(dto.IsDeleted ?? dto.isDeleted),
      CreatedAt: dto.CreatedAt ?? dto.createdAt,
      UpdatedAt: dto.UpdatedAt ?? dto.updatedAt,
      CreatedBy: dto.CreatedBy ?? dto.createdBy ?? null,
      UpdatedBy: dto.UpdatedBy ?? dto.updatedBy ?? null,
      DeletedAt: dto.DeletedAt ?? dto.deletedAt ?? null,
      DeletedBy: dto.DeletedBy ?? dto.deletedBy ?? null
    };
  }

  private mapTimeSlot(dto: any): TimeSlot {
    if (!dto) {
      return dto;
    }
    return {
      Id: dto.Id ?? dto.id,
      ScheduleId: dto.ScheduleId ?? dto.scheduleId,
      Date: dto.Date ?? dto.date,
      StartTime: this.normalizeTime(dto.StartTime ?? dto.startTime),
      EndTime: this.normalizeTime(dto.EndTime ?? dto.endTime),
      Capacity: dto.Capacity ?? dto.capacity ?? 0,
      BookedCount: dto.BookedCount ?? dto.bookedCount ?? 0
    };
  }

  private normalizeTime(value: unknown): string {
    if (value == null) return '00:00:00';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
      if (/^\d{2}:\d{2}:\d{2}/.test(trimmed)) return trimmed.slice(0, 8);
      return trimmed;
    }
    return String(value);
  }

  private handleError(error: any) {
    this.errorService.showError('Erro ao acessar agendas.');
    return throwError(() => error);
  }
}
